/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  PLATGEN_SINGULAR_SDK_KEY,
  PLATGEN_SINGULAR_SDK_SECRET,
} from 'config/envVars';
import { captureMessage } from 'lib/reporter/utils/captureMessage';

/**
 * Generate productId from hostname by reversing the domain parts.
 * e.g., app.sleepio.com -> com.sleepio.app
 * This must match the Bundle ID configured in Singular dashboard.
 */
const getProductIdFromHostname = (): string => {
  return window.location.hostname
    .split('.')
    .reverse()
    .join('.');
};

export const SingularInit: React.FC = () => {
  const isInitialized = useRef(false);
  const windowObject = window as any;
  const location = useLocation();

  // Initialize SDK once on mount
  useEffect(() => {
    try {
      if (isInitialized.current) {
        return;
      }

      if (!PLATGEN_SINGULAR_SDK_KEY || !PLATGEN_SINGULAR_SDK_SECRET) {
        console.warn(
          'Singular SDK credentials not provided, skipping initialization'
        );
        isInitialized.current = true;
        return;
      }

      if (!windowObject.singularSdk || !windowObject.SingularConfig) {
        console.warn(
          'Singular SDK not loaded yet. Check that the script is in the <head> section.'
        );
        return;
      }

      // Use reversed hostname as productId to match Singular dashboard config
      // e.g., app.sleepio.com -> com.sleepio.app
      const productId = getProductIdFromHostname();

      const config = new windowObject.SingularConfig(
        PLATGEN_SINGULAR_SDK_KEY,
        PLATGEN_SINGULAR_SDK_SECRET,
        productId
      ).withInitFinishedCallback();
      windowObject.singularSdk.init(config);
      isInitialized.current = true;
      // Below is needed to track the initial app open event
      // and register it to Singular.
      windowObject.singularSdk.event('app_opened', {
        path: windowObject.location.pathname,
        referrer: document.referrer,
      });
    } catch (error) {
      captureMessage(error);
    }
  }, [
    windowObject.SingularConfig,
    windowObject.location.pathname,
    windowObject.singularSdk,
  ]); // Empty dependency - runs once

  // Track page visits on route changes (NOT on initial mount)
  // We might be able to move this to the HOC that wraps Routes
  // so we have a unified place for route change tracking across analytics services.
  useEffect(() => {
    if (isInitialized.current && windowObject.singularSdk) {
      windowObject.singularSdk.pageVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Runs when pathname changes

  return null;
};
